/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ACCOUNT_DATA_SERVICE, AccountDataService } from '@app/app/page-modules/account/services/account-data.service';
import { USER_GROUPS_DATA_SERVICE, UserGroupsDataService } from '@app/app/page-modules/admin/services/groups/groups-data.service';
import { FileUploadHelper } from '@app/app/shared/helpers/file-upload-helper';
import { UserGroups, displayableGroupTypeLabel } from '@app/app/shared/models/admin/group/user-groups.model';
import { SafeHtmlPipe } from '@app/app/shared/pipes/safe-html.pipe';
import { TOAST_NOTIFICATION_SERVICE, ToastService } from '@app/app/shared/services/toast-notifications/toast-service';
import { AutocompleteSearchOption } from '@app/app/shared/styleguide/components/autocomplete-search/sg-autocomplete-search.component';
import { PtlV3DialogShellComponent } from '@app/app/shared/styleguide/components/dialog-shell/ptl-v3-dialog-shell.component';
import { UploadFileRejection } from '@app/app/shared/styleguide/components/upload-file-area/upload-file-area.component';
import { StyleguideModule } from '@app/app/shared/styleguide/styleguide.module';
import { LucideFileSpreadsheet, LucideX } from '@lucide/angular';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { Subject, merge } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';

/** Members processed by the import, or `undefined` when the dialog was dismissed. */
export type V3AlumniCsvUploadDialogResult = number | undefined;

const GROUPS_SEARCH_PAGE_SIZE = 10;
const ACCEPTED_FILE_TYPES = '.csv';
const SEARCH_DEBOUNCE_MS = 500;

@Component({
  selector: 'ptl-v3-alumni-csv-upload-dialog',
  templateUrl: './v3-alumni-csv-upload-dialog.component.html',
  styleUrls: ['./v3-alumni-csv-upload-dialog.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslocoModule, StyleguideModule, PtlV3DialogShellComponent, SafeHtmlPipe],
})
export class V3AlumniCsvUploadDialogComponent {
  protected readonly LucideX = LucideX;
  protected readonly LucideFileSpreadsheet = LucideFileSpreadsheet;
  protected readonly acceptedFileTypes = ACCEPTED_FILE_TYPES;

  protected readonly csvFile = signal<File | null>(null);
  protected readonly csvErrorKey = signal<string | null>(null);
  protected readonly groupInvalid = signal(false);
  protected readonly isLoading = signal(false);
  protected readonly loadingGroups = signal(false);

  private readonly selectedGroup = signal<UserGroups | null>(null);
  private readonly searchedGroups = signal<UserGroups[]>([]);

  protected readonly selectedGroupUid = computed<string | null>(() => this.selectedGroup()?._id ?? null);

  protected readonly groupOptions = computed<AutocompleteSearchOption<string>[]>(() => {
    const byUid = new Map<string, UserGroups>();
    const selected = this.selectedGroup();
    if (selected) {
      byUid.set(selected._id, selected);
    }
    for (const group of this.searchedGroups()) {
      byUid.set(group._id, group);
    }
    return Array.from(byUid.values()).map((group) => ({
      value: group._id,
      label: group.title,
      sublabel: displayableGroupTypeLabel(group.type),
    }));
  });

  private readonly dialogRef = inject(MatDialogRef<V3AlumniCsvUploadDialogComponent, V3AlumniCsvUploadDialogResult>);
  private readonly accountService = inject<AccountDataService>(ACCOUNT_DATA_SERVICE);
  private readonly userGroupsService = inject<UserGroupsDataService>(USER_GROUPS_DATA_SERVICE);
  private readonly toastService = inject<ToastService>(TOAST_NOTIFICATION_SERVICE);
  private readonly transloco = inject(TranslocoService);

  /** Typing is debounced; opening the panel is a discrete act and should not wait on it. */
  private readonly typedTerm$ = new Subject<string>();
  private readonly firstOpen$ = new Subject<string>();
  /** The picker clears its own search box on reopen without saying so; this is what the list was last filtered by. */
  private lastSearchTerm = '';

  constructor() {
    merge(this.firstOpen$, this.typedTerm$.pipe(debounceTime(SEARCH_DEBOUNCE_MS)))
      .pipe(
        switchMap((term) => this.userGroupsService.searchUserGroups(0, GROUPS_SEARCH_PAGE_SIZE, term)),
        takeUntilDestroyed(),
      )
      .subscribe(({ isSuccess, value }) => {
        if (isSuccess) {
          this.searchedGroups.set(value.content);
        }
        this.loadingGroups.set(false);
      });
  }

  protected closeDialog(result?: V3AlumniCsvUploadDialogResult): void {
    if (!this.isLoading()) {
      this.dialogRef.close(result);
    }
  }

  protected onFileSelected(file: File): void {
    this.csvErrorKey.set(null);
    this.csvFile.set(file);
  }

  protected onFilesRejected(reason: UploadFileRejection): void {
    if (reason === 'WRONG_TYPE') {
      this.csvErrorKey.set('translations.membersNew.controlPanel.alumniUpload.error.invalidFileType');
      return;
    }
    this.toastService.showFail(this.transloco.translate('translations.uploadFileCountLimitError'));
  }

  protected removeSelectedFile(): void {
    this.csvErrorKey.set(null);
    this.csvFile.set(null);
  }

  protected onGroupPanelOpened(): void {
    if (this.searchedGroups().length && !this.lastSearchTerm) {
      return;
    }
    this.lastSearchTerm = '';
    this.loadingGroups.set(true);
    this.firstOpen$.next('');
  }

  protected onGroupSearchTermChange(term: string): void {
    this.lastSearchTerm = term ?? '';
    this.loadingGroups.set(true);
    this.typedTerm$.next(this.lastSearchTerm);
  }

  protected onGroupSelectionChange(value: string | string[] | null): void {
    const uid = Array.isArray(value) ? (value[0] ?? null) : value;
    if (!uid) {
      this.selectedGroup.set(null);
      return;
    }
    const group = this.searchedGroups().find((item) => item._id === uid) ?? null;
    if (group) {
      this.selectedGroup.set(group);
      this.groupInvalid.set(false);
    }
  }

  protected uploadAlumniUsers(): void {
    const group = this.selectedGroup();
    const file = this.csvFile();

    this.groupInvalid.set(!group);
    this.csvErrorKey.set(file ? null : 'translations.membersNew.controlPanel.alumniUpload.error.fileRequired');

    if (!group || !file) {
      return;
    }

    if (!FileUploadHelper.isValidCsvFile(file)) {
      this.csvErrorKey.set('translations.membersNew.controlPanel.alumniUpload.error.invalidFileType');
      return;
    }

    this.isLoading.set(true);
    this.dialogRef.disableClose = true;
    this.accountService.alumniUploadCsv({ groupUid: group._id, file }).subscribe(({ isSuccess, value, error }) => {
      this.isLoading.set(false);
      this.dialogRef.disableClose = false;
      if (!isSuccess) {
        this.toastService.showFail(error);
        return;
      }
      const processed = value?.processed ?? 0;
      this.closeDialog(processed);
      this.toastService.showSuccess(
        this.transloco.translate('translations.members.alumni.message.success.csvFileAdded').replace('{number}', processed.toString()),
      );
    });
  }
}
